import { useEffect, useState, useCallback } from 'react'
import { useSessionContext } from '../contexts/SessionContext'
import { logger } from '../utils/helpers/logger'
import { supabase, uploadFile } from '../utils/helpers/supabase.helpers'
import { AGAMI_INDOOR_BUCKET } from '../utils/constants/db.constants'
import { useGeneralToast } from './toast'
import { useFeatureFlag } from './authorization'
import { FEATURE_FLAGS } from '../utils/constants/features.constants'

interface UseAgamiIndoorConfigReturn {
  config: string
  isDownloading: boolean
  isSaving: boolean
  save: (newConfig: string) => Promise<void>
  download: () => Promise<void>
}

export const useAgamiIndoorConfig = (): UseAgamiIndoorConfigReturn => {
  const { user } = useSessionContext()
  const agamiIndoorFlag = useFeatureFlag(FEATURE_FLAGS.AGAMI_INDOOR)
  const toast = useGeneralToast()

  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [config, setConfig] = useState<string>('')

  const download = useCallback(async (): Promise<void> => {
    if (!user) return
    try {
      setIsDownloading(true)
      const { data } = await supabase.storage
        .from(AGAMI_INDOOR_BUCKET)
        .download(`${user.id}/config.json?bust=${Date.now()}`)
      if (!data) return
      const text = await data.text()
      const json = JSON.parse(text)
      setConfig(json)
    } catch (error) {
      logger.error(error, 'IndoorConfig')
    } finally {
      setIsDownloading(false)
    }
  }, [user])

  const save = useCallback(
    async (newConfig: string): Promise<void> => {
      if (!user) return
      setIsSaving(true)

      try {
        const { error } = await uploadFile({
          filename: `${user.id}/config.json`,
          bucketId: AGAMI_INDOOR_BUCKET,
          file: JSON.stringify(newConfig),
          cacheControl: '0',
          contentType: 'application/json',
          upsert: true,
        })

        if (error) {
          logger.error(error, 'IndoorConfig')
          toast.error('Something went wrong')
          return
        }

        setConfig(newConfig)
        toast.success('Config saved!')
      } catch (error) {
        logger.error(error, 'IndoorConfig')
      } finally {
        setIsSaving(false)
      }
    },
    [user, uploadFile]
  )

  useEffect(() => {
    if (!agamiIndoorFlag || !user) return
    download()
  }, [agamiIndoorFlag, user])

  return {
    config,
    isDownloading,
    isSaving,
    save,
    download,
  }
}
